import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RollData } from '../types';
import { buyRoll } from '../lib/gameEngine';
import { usePlayerSession } from '../hooks/usePlayerSession';
import { useRouter, ViewMode } from '../lib/router';
import { TopBar } from './TopBar';
import { MainMenu } from './MainMenu';
import { OrderRollsMenu } from './OrderRollsMenu';
import { InventoryMenu } from './InventoryMenu';
import { AlbumMenu } from './AlbumMenu';
import { LeaderboardMenu } from './LeaderboardMenu';
import { AchievementsMenu } from './AchievementsMenu';
import { HuntingView } from './HuntingView';
import { PrivacyPolicy } from './PrivacyPolicy';
import { TermsOfService } from './TermsOfService';
import { Footer } from './Footer';
import { SaveAccountModal } from './SaveAccountModal';
import { FallingCoinRolls } from './FallingCoinRolls';
import { FallingCoins } from './FallingCoins';
import { MaintenanceScreen } from './MaintenanceScreen';
import './App.css';

const IS_MAINTENANCE = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

export type { ViewMode };

export const App: React.FC = () => {
  if (IS_MAINTENANCE) {
    return <MaintenanceScreen />;
  }

  const { currentView, navigate } = useRouter();
  const [currentRoll, setCurrentRoll] = useState<RollData | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isRollFinished, setIsRollFinished] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Player session, auth, active rolls, and user album state from hook
  const {
    profile,
    setProfile,
    username,
    activeRolls,
    setsData,
    userAlbum,
    setUserAlbum,
    allowanceStatus,
    isInitialLoaded,
    handleClaimAllowance,
    handleClaimSetReward,
    refreshProfileAndRolls,
    handleSignOut
  } = usePlayerSession();

  // Auth Save UI state
  const [isSaveModalOpen, setIsSaveModalOpen] = useState<boolean>(false);
  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(false);
  const [triggerCoinName, setTriggerCoinName] = useState<string | undefined>(undefined);

  // Flash overlay state
  const [activeFlashBg, setActiveFlashBg] = useState<string | null>(null);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTriggerScreenFlash = useCallback((flashColor: string) => {
    setActiveFlashBg(flashColor);
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = setTimeout(() => {
      setActiveFlashBg(null);
    }, 550);
  }, []);

  const handleBuyAndHuntRoll = useCallback(async (rollType = 'standard_2euro') => {
    try {
      setLoading(true);
      setErrorMessage(null);
      localStorage.setItem('last_hunted_roll_type', rollType);
      const res = await buyRoll(rollType);
      if (res.success && res.data) {
        setCurrentRoll(res.data.roll);
        setCurrentIndex(res.data.currentIndex || 0);
        setIsRollFinished(false);
        setProfile(prev => ({
          ...prev,
          wallet: res.data!.wallet,
          dailyFreeRollsLeft: res.data!.dailyFreeRollsLeft
        }));
        navigate('hunting');
        refreshProfileAndRolls();
      } else if (res.error) {
        setErrorMessage(res.error);
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to buy roll');
    } finally {
      setLoading(false);
    }
  }, [navigate, refreshProfileAndRolls, setProfile]);

  const handleBackToBank = useCallback(() => {
    refreshProfileAndRolls();
    setCurrentRoll(null);
    setIsRollFinished(false);
    setCurrentIndex(0);
    navigate('order_rolls');
  }, [refreshProfileAndRolls, navigate]);

  // Auto-restore active roll when landing on or refreshing /hunting
  useEffect(() => {
    if (currentView === 'hunting' && !currentRoll && isInitialLoaded) {
      const activeRollList = Object.values(activeRolls);
      if (activeRollList.length > 0) {
        const lastRollType = localStorage.getItem('last_hunted_roll_type');
        const activeItem = (lastRollType && activeRolls[lastRollType])
          ? activeRolls[lastRollType]
          : activeRollList[0];

        setCurrentRoll(activeItem.roll);
        setCurrentIndex(activeItem.currentIndex);
        setIsRollFinished(false);
      } else {
        // Truly no active roll in database, navigate to order_rolls
        navigate('order_rolls', { replace: true });
      }
    }
  }, [currentView, currentRoll, isInitialLoaded, activeRolls, navigate]);

  const activeRollsCount = Object.keys(activeRolls).length;

  return (
    <>
      {currentView === 'hunting' ? <FallingCoins /> : <FallingCoinRolls />}
      <div className={`container ${(currentView === 'inventory' || currentView === 'achievements') ? 'wide-container' : ''}`}>
      {/* Top Bar */}
      <TopBar
        wallet={profile.wallet}
        isGuest={profile.isGuest}
        username={username}
        allowanceStatus={allowanceStatus}
        onClaimAllowance={handleClaimAllowance}
        onOpenSignIn={() => {
          setTriggerCoinName(undefined);
          setIsSaveModalOpen(true);
        }}
        onSignOut={handleSignOut}
      />

      {errorMessage && (
        <div style={{
          backgroundColor: '#fee2e2',
          border: '1.5px solid #dc2626',
          color: '#b91c1c',
          padding: '10px 16px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '14px',
          fontWeight: '600',
          textAlign: 'center',
          width: '100%',
          maxWidth: '600px'
        }}>
          ⚠️ {errorMessage}
        </div>
      )}

      {/* VIEW 1: Main Menu */}
      {currentView === 'home' && (
        <MainMenu
          onNavigate={(view) => {
            refreshProfileAndRolls();
            navigate(view);
          }}
          activeRollsCount={activeRollsCount}
        />
      )}

      {/* VIEW 2: Order Coin Rolls (Bank Counter) */}
      {currentView === 'order_rolls' && (
        <OrderRollsMenu
          dailyFreeRollsLeft={profile.dailyFreeRollsLeft}
          wallet={profile.wallet}
          activeRolls={activeRolls}
          onBack={() => {
            refreshProfileAndRolls();
            navigate('home');
          }}
          onBuyStandardRoll={() => handleBuyAndHuntRoll('standard_2euro')}
          onBuyRoll={handleBuyAndHuntRoll}
          loading={loading}
        />
      )}

      {/* VIEW 3: Hunting / Inspection Screen */}
      {currentView === 'hunting' && (
        <HuntingView
          currentRoll={currentRoll}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          isRollFinished={isRollFinished}
          setIsRollFinished={setIsRollFinished}
          setCurrentRoll={setCurrentRoll}
          profile={profile}
          setProfile={setProfile}
          setsData={setsData}
          userAlbum={userAlbum}
          setUserAlbum={setUserAlbum}
          loading={loading}
          onBackToBank={handleBackToBank}
          onOpenSaveModal={() => setIsSaveModalOpen(true)}
          triggerCoinName={triggerCoinName}
          setTriggerCoinName={setTriggerCoinName}
          isBannerVisible={isBannerVisible}
          setIsBannerVisible={setIsBannerVisible}
          refreshProfileAndRolls={refreshProfileAndRolls}
          onTriggerScreenFlash={handleTriggerScreenFlash}
        />
      )}

      {/* VIEW 4: Inventory & Market Sales */}
      {currentView === 'inventory' && (
        <InventoryMenu
          onBack={() => {
            refreshProfileAndRolls();
            navigate('home');
          }}
          onWalletUpdated={(newWallet) => {
            setProfile(prev => ({ ...prev, wallet: newWallet }));
          }}
        />
      )}

      {/* VIEW 5: Collection Album */}
      {currentView === 'album' && (
        <AlbumMenu
          initialUserAlbum={userAlbum}
          initialSets={setsData}
          onBack={() => {
            refreshProfileAndRolls();
            navigate('home');
          }}
          onClaimSetReward={handleClaimSetReward}
        />
      )}

      {/* VIEW 6: Leaderboard */}
      {currentView === 'leaderboard' && (
        <LeaderboardMenu
          isGuest={profile.isGuest}
          onBack={() => {
            refreshProfileAndRolls();
            navigate('home');
          }}
        />
      )}

      {/* VIEW: Achievements */}
      {currentView === 'achievements' && (
        <AchievementsMenu
          onBack={() => {
            refreshProfileAndRolls();
            navigate('home');
          }}
          onWalletUpdated={(newWallet) => {
            setProfile(prev => ({ ...prev, wallet: newWallet }));
          }}
        />
      )}

      {/* VIEW 7: Privacy Policy */}
      {currentView === 'privacy' && (
        <PrivacyPolicy
          onNavigate={(view) => {
            refreshProfileAndRolls();
            navigate(view);
          }}
        />
      )}

      {/* VIEW 8: Terms of Service */}
      {currentView === 'terms' && (
        <TermsOfService
          onNavigate={(view) => {
            refreshProfileAndRolls();
            navigate(view);
          }}
        />
      )}

      {/* Footer on Main Page */}
      {currentView === 'home' && <Footer onNavigate={(view) => navigate(view)} />}

      {/* Rarity-Tinted Screen Flash Overlay */}
      {activeFlashBg && (
        <div
          className="screen-flash-overlay"
          style={{ background: activeFlashBg }}
        />
      )}

      {/* 1-Click Google & Discord Sign In Modal */}
      <SaveAccountModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        triggerCoinName={triggerCoinName}
        onNavigate={(view) => navigate(view)}
      />
    </div>
    </>
  );
};
